import { useCallback, useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { Header } from './Header';
import { useCurrentUser } from '../../hooks/useApi';
import { ImpersonateProvider } from '../../contexts/ImpersonateContext';

const COLLAPSE_KEY = 'sidebarCollapsed';

function readCollapsed(): boolean {
  try {
    return localStorage.getItem(COLLAPSE_KEY) === '1';
  } catch {
    // Private browsing / blocked site data — fall back to the expanded default.
    return false;
  }
}

export function MainLayout() {
  const currentUser = useCurrentUser();
  const isAdmin = currentUser.data?.isAdmin ?? false;
  const isMarketing = currentUser.data?.isMarketing ?? false;

  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  // Navigating always dismisses the mobile drawer, including back/forward moves
  // that no NavLink onClick would catch.
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const toggleCollapsed = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(COLLAPSE_KEY, next ? '1' : '0');
      } catch {
        // Preference is a convenience; ignore storage failures.
      }
      return next;
    });
  }, []);

  const closeMobile = useCallback(() => setMobileOpen(false), []);

  return (
    <ImpersonateProvider realIsAdmin={isAdmin} realIsMarketing={isMarketing}>
      <div className="flex h-screen overflow-hidden">
        <Sidebar collapsed={collapsed} mobileOpen={mobileOpen} onCloseMobile={closeMobile} />

        {/* Backdrop for the mobile drawer */}
        {mobileOpen && (
          <div
            className="fixed inset-0 z-30 bg-gray-900/50 lg:hidden"
            onClick={closeMobile}
            aria-hidden="true"
          />
        )}

        <div className="flex-1 flex flex-col overflow-hidden min-w-0">
          <Header
            collapsed={collapsed}
            onToggleCollapsed={toggleCollapsed}
            onOpenMobile={() => setMobileOpen(true)}
          />
          <main className="flex-1 overflow-y-auto p-4 sm:p-6 bg-gray-50 dark:bg-gray-950">
            <Outlet />
          </main>
        </div>
      </div>
    </ImpersonateProvider>
  );
}
